import { Box, useMediaQuery, IconButton } from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import { Edit, MarkUnreadChatAlt } from '@mui/icons-material';
import { IResponse, ISortDirection, IBuildCommunication } from '../../../models';
import { useQuery } from 'react-query';
import { Button, MobileTable, Pagination, Table } from '../../../components';
import { BuildCommunicationsFilters } from './build-communications-filters';
import { getBuildCommunications } from '../../../fetch';
import { useHistory } from 'react-router-dom';

interface IBuildCommunicationsListProps {
  openBuilds: boolean;
}

export const BuildCommunicationsList = ({ openBuilds }: IBuildCommunicationsListProps) => {
  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    lotNumber?: number | string;
    restrictToBuildsWithUnread?: boolean | null;
  } | null>({});
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>('');
  const [selectedLotId, setSelectedLotId] = useState<string>('');
  const [hasUnreadMessages, setHasUnreadMessages] = useState<string | null>(null);

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const [selectedSort, setSelectedSort] = useState<string>('Neighborhood');
  const [sortDirection, setSortDirection] = useState<{
    Neighborhood?: ISortDirection;
    LotNumber?: ISortDirection;
  }>({
    Neighborhood: 'Asc'
  });

  // Reset filters and pagination when openBuilds changes
  useEffect(() => {
    setPage(0);
    setFilters({});
    setSelectedNeighborhoodId('');
    setSelectedLotId('');
    setHasUnreadMessages(null);
  }, [openBuilds]);

  const {
    isLoading,
    isFetching,
    data: buildCommunicationsData
  } = useQuery<IResponse<IBuildCommunication[]>, Error>(
    ['getBuildCommunications', page, perPage, selectedSort, sortDirection, filters, openBuilds],
    () =>
      getBuildCommunications({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        restrictToOpenBuilds: openBuilds,
        ...filters
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleEdit = (original: IBuildCommunication) => {
    history.push(`/production/build-communications/homeowner-messaging/${original.buildId}`);
  };

  const data = useMemo(() => buildCommunicationsData?.records ?? [], [buildCommunicationsData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Neighborhood',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'Neighborhood',
        isServerSortedDesc: sortDirection.Neighborhood === 'Desc',
        handleClickColumn: () => handleClickColumn('Neighborhood')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Unread Messages',
        accessor: 'hasUnreadMessage',
        sort: false,
        handleClickColumn: () => {},
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuildCommunication } };
        }) => {
          return (
            <Box sx={{ pl: 2 }}>
              {original.hasUnreadMessage && (
                <MarkUnreadChatAlt color="secondary" sx={{ fontSize: 20 }} />
              )}
            </Box>
          );
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuildCommunication } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id={`edit-build-communication-${original.jobId}`}
                type="button"
                color="primary"
                startIcon={<Edit />}
                onClick={() => handleEdit(original)}
              ></Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort]);

  const renderMobileActions = (item: IBuildCommunication) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
        {item.hasUnreadMessage && (
          <MarkUnreadChatAlt
            color="secondary"
            sx={{
              fontSize: 18,
              marginRight: '4px',
              marginTop: '4px'
            }}
          />
        )}
        <IconButton
          sx={{ padding: '2px 5px' }}
          color="primary"
          onClick={e => {
            e.stopPropagation();
            handleEdit(item);
          }}
          size="large"
        >
          <Edit />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <BuildCommunicationsFilters
        isLoading={isLoading || isFetching}
        openBuilds={openBuilds}
        selectedNeighborhoodId={selectedNeighborhoodId}
        selectedLotId={selectedLotId}
        setSelectedNeighborhoodId={setSelectedNeighborhoodId}
        setSelectedLotId={setSelectedLotId}
        hasUnreadMessages={hasUnreadMessages}
        setHasUnreadMessages={setHasUnreadMessages}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              neighborhoodId: '',
              lotNumber: '',
              restrictToBuildsWithUnread: null 
            });
          } else {
            setPage(0);
            setFilters({
              neighborhoodId: selectedNeighborhoodId || undefined,
              lotNumber: selectedLotId || undefined,
              restrictToBuildsWithUnread: hasUnreadMessages === null ? null : hasUnreadMessages === 'true' ? true : false
            });
          }
        }}
      />
      <Table
        columns={columns}
        data={data}
        isLoading={isLoading || isFetching}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header && c.Header !== 'Unread Messages') // Exclude Unread Messages column on mobile
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          handleView: (val: IBuildCommunication) => handleEdit(val),
          getAccordionHeader: (item: IBuildCommunication) =>
            `${item.displayName} Lot ${item.lotNumber}`,
          renderActions: (item: IBuildCommunication) => renderMobileActions(item)
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && !isFetching && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </>
  );
};
